import React, { useState } from 'react';
import { WellInflowInfo } from '../../../../store/model/calculations/calculation';
import useBasicCalculationsFacade from '../../../../facade/calculation/calculation-output/basic-calculation.facade';
import { LABELS } from '../../../../constants/labels/basicCals.labels';
import { useAppSelector } from '../../../../store/hooks/StoreHooks';

interface IPRTableProps {
    wellInflowData?: WellInflowInfo[];
    iprSelected?: string;
    onRadioChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
}

const IPRTable: React.FC<IPRTableProps> = ({ wellInflowData, iprSelected, onRadioChange }) => {
    // Handle missing or empty data safely
    const wellInflowData11 = wellInflowData?.[0];
    const { updateIPRSelection } = useBasicCalculationsFacade();
    const { customerEnabled, customerCreated } = useAppSelector((state) => state.auth);
    const { projectReadOnly } = useAppSelector((state) => state.projectHeader);

    // Handle radio button change
    const handleRadioChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (onRadioChange) {
            onRadioChange(event);
        } else {
            updateIPRSelection(event?.target?.value);
        }
    };

    // Define table data structure dynamically from API response
    const tableData = [
        {
            parameter: `${LABELS?.maxFLowRateBpd}`,
            Wiggins: wellInflowData11?.mxFlowRate4,
            Vogel: wellInflowData11?.mxFlowRate1,
            Composite: wellInflowData11?.mxFlowRate2,
            Gilbert: wellInflowData11?.mxFlowRate3,
        },
        {
            parameter: `${LABELS?.additionalBpd}`,
            Wiggins: wellInflowData11?.addtionalBPD4,
            Vogel: wellInflowData11?.addtionalBPD1,
            Composite: wellInflowData11?.addtionalBPD2,
            Gilbert: wellInflowData11?.addtionalBPD3,
        },
        {
            parameter: `${LABELS?.PI}`,
            Wiggins: wellInflowData11?.piStrVal4,
            Vogel: wellInflowData11?.piStrVal1,
            Composite: wellInflowData11?.piStrVal2,
            Gilbert: wellInflowData11?.piStrVal3,
        },
        {
            parameter: `${LABELS?.flowingBottomHolePressure}`,
            Wiggins: wellInflowData11?.fbhpStrVal4,
            Vogel: wellInflowData11?.fbhpStrVal1,
            Composite: wellInflowData11?.fbhpStrVal2,
            Gilbert: wellInflowData11?.fbhpStrVal3,
        },
    ];

    return (
        <div id="collapseWellInfloCal" aria-expanded="true" className="scrollable m-h-[400px] relative overflow-auto">
            <table>
                <thead>
                    <tr>
                        <th className="px-[24px] py-[16px] text-[12px] text-neutral-25 text-left">
                            <div>{LABELS?.iprSelected} {iprSelected === 'ConstantPI' ? 'Gilbert (Cont. PI)' : iprSelected}</div>
                        </th>
                        <th className="px-[24px] py-[16px] text-[12px] text-neutral-25">
                            <div className="flex items-center gap-[8px]">
                                {
                                    ((!customerEnabled && !projectReadOnly) || (customerEnabled && customerCreated)) &&
                                    <input
                                        type="radio"
                                        id="Wiggins"
                                        name="IPR"
                                        value={LABELS?.wiggins}
                                        checked={iprSelected === LABELS?.wiggins}
                                        onChange={handleRadioChange}
                                        disabled={iprSelected === "ConstantPI" || iprSelected === "Composite"}
                                    />
                                }

                                <label htmlFor="Wiggins">{LABELS?.wiggins}</label>
                            </div>
                        </th>
                        <th className="px-[24px] py-[16px] text-[12px] text-neutral-25">
                            <div className="flex items-center gap-[8px]">
                                {
                                    ((!customerEnabled && !projectReadOnly) || (customerEnabled && customerCreated)) &&
                                    <input
                                        type="radio"
                                        id="Vogel"
                                        name="IPR"
                                        value={LABELS?.vogel}
                                        checked={iprSelected === LABELS?.vogel}
                                        onChange={handleRadioChange}
                                        disabled={iprSelected === "ConstantPI" || iprSelected === "Composite"}
                                    />
                                }

                                <label htmlFor="Vogel">{LABELS?.vogel}</label>
                            </div>
                        </th>
                        <th className="px-[24px] py-[16px] text-[12px] text-neutral-25">{LABELS?.composite}</th>
                        <th className="px-[24px] py-[16px] text-[12px] text-neutral-25">{LABELS?.gilbert}</th>
                    </tr>
                </thead>
                <tbody>
                    {tableData.map((row, index) => (
                        <tr key={index}>
                            <td className="px-[24px] py-[9px] text-neutral-25 text-[14px]">{row.parameter}</td>
                            <td className="px-[24px] py-[9px] text-neutral-25 text-[14px]">{row.Wiggins}</td>
                            <td className="px-[24px] py-[9px] text-neutral-25 text-[14px]">{row.Vogel}</td>
                            <td className="px-[24px] py-[9px] text-neutral-25 text-[14px]">{row.Composite}</td>
                            <td className="px-[24px] py-[9px] text-neutral-25 text-[14px]">{row.Gilbert}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
};

export default IPRTable;
